import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@shared/ui/alert-dialog';
import { Button, buttonVariants } from '@shared/ui/button';
import { cn } from '@shared/lib/utils';
import { useIsMobile } from '@shared/hooks/use-breakpoint';
import { BottomSheet } from '@shared/ui/adaptive/BottomSheet';

export interface ConfirmDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: React.ReactNode;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'destructive';
  onConfirm: () => void;
  onCancel?: () => void;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  onConfirm,
  onCancel,
}) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <>
        {trigger}
        <BottomSheet
          open={!!open}
          onOpenChange={(v) => {
            if (!v && onCancel) onCancel();
            onOpenChange?.(v);
          }}
          title={title}
          description={description}
        >
          <div className="flex flex-col gap-2 pt-2">
            <Button
              variant={variant === 'destructive' ? 'destructive' : 'default'}
              size="lg"
              className="w-full h-11 text-sm font-medium"
              onClick={() => {
                onConfirm();
                onOpenChange?.(false);
              }}
            >
              {confirmLabel}
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="w-full h-11 text-sm font-medium"
              onClick={() => {
                if (onCancel) onCancel();
                onOpenChange?.(false);
              }}
            >
              {cancelLabel}
            </Button>
          </div>
        </BottomSheet>
      </>
    );
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      {trigger && <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>}
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className={cn(variant === 'destructive' && buttonVariants({ variant: 'destructive' }))}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

